// LiveAttendanceDashboard - Enhanced real-time presence for Trainers/Admins
// Shows 4 statuses: active, external, idle, absent
// Supports session mode switching and manual marking
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Loader2, Users, Wifi, WifiOff, Clock, AlertTriangle,
  RefreshCw, Monitor, UserCheck, BookOpen, Wrench, FileCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL_MS = 15000;

const STATUS_CONFIG = {
  active:   { label: 'Actif',      color: '#22c55e', bgColor: 'rgba(34,197,94,0.1)',  icon: Wifi },
  external: { label: 'Hors plateforme', color: '#3b82f6', bgColor: 'rgba(59,130,246,0.1)', icon: Monitor },
  idle:     { label: 'Inactif',     color: '#f59e0b', bgColor: 'rgba(245,158,11,0.1)', icon: Clock },
  absent:   { label: 'Absent',      color: '#ef4444', bgColor: 'rgba(239,68,68,0.1)',  icon: WifiOff },
};

const MODE_CONFIG = {
  theoretical: { label: 'Cours théorique', icon: BookOpen, color: '#8b5cf6' },
  practical:   { label: 'Travaux pratiques', icon: Wrench, color: '#f59e0b' },
  exam:        { label: 'Examen', icon: FileCheck, color: '#ef4444' },
};

export default function LiveAttendanceDashboard({ sessionId, workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingUserId, setMarkingUserId] = useState(null);
  const intervalRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchPresence = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/presence/session/${sessionId}?workspace_id=${workspaceId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Erreur de chargement');
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionId || !workspaceId) return;
    fetchPresence();
    intervalRef.current = setInterval(fetchPresence, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [sessionId, workspaceId]);

  const changeMode = async (mode) => {
    try {
      await fetch(`${API_URL}/api/presence/session/${sessionId}/mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mode }),
      });
      fetchPresence();
    } catch (err) {
      console.error('Failed to change mode:', err);
    }
  };

  const markPresent = async (userId) => {
    setMarkingUserId(userId);
    try {
      await fetch(`${API_URL}/api/presence/session/${sessionId}/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, status: 'external' }),
      });
      fetchPresence();
    } catch (err) {
      console.error('Failed to mark present:', err);
    } finally {
      setMarkingUserId(null);
    }
  };

  if (!sessionId || !workspaceId) return null;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const currentMode = data?.mode || 'theoretical';
  const currentModeConfig = MODE_CONFIG[currentMode] || MODE_CONFIG.theoretical;
  const ModeIcon = currentModeConfig.icon;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-primary" />
            Présence en Direct
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchPresence} className="gap-1">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </Button>
          </div>
        </div>

        {/* Session Mode Selector */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-sm text-muted-foreground mr-1">Mode :</span>
          {Object.entries(MODE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const isActive = currentMode === key;
            return (
              <button
                key={key}
                onClick={() => changeMode(key)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                style={{
                  backgroundColor: isActive ? cfg.color + '20' : 'transparent',
                  color: isActive ? cfg.color : '#94a3b8',
                  border: `1.5px solid ${isActive ? cfg.color : '#e2e8f0'}`,
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                <Icon className="h-3.5 w-3.5" />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary Stats - 4 statuses */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['active', 'external', 'idle', 'absent'].map((key) => {
            const cfg = STATUS_CONFIG[key];
            const Icon = cfg.icon;
            const count = data?.[key] || 0;
            return (
              <div
                key={key}
                className="flex flex-col items-center p-3 rounded-xl transition-all"
                style={{ backgroundColor: cfg.bgColor, border: `1px solid ${cfg.color}30` }}
              >
                <Icon className="h-5 w-5 mb-1" style={{ color: cfg.color }} />
                <span className="text-2xl font-bold" style={{ color: cfg.color }}>{count}</span>
                <span className="text-xs text-muted-foreground">{cfg.label}</span>
              </div>
            );
          })}
        </div>

        {/* Student List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(!data?.students || data.students.length === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun candidat connecté</p>
            </div>
          ) : (
            data.students.map((student) => {
              const cfg = STATUS_CONFIG[student.status] || STATUS_CONFIG.absent;
              const Icon = cfg.icon;
              const canMark = student.status === 'idle' || student.status === 'absent';

              return (
                <div
                  key={student.user_id}
                  className="flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-sm"
                  style={{ borderLeftColor: cfg.color, borderLeftWidth: '3px' }}
                >
                  {/* Status dot */}
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: cfg.color,
                      boxShadow: student.status === 'active' ? `0 0 8px ${cfg.color}` : 'none',
                      animation: student.status === 'active' ? 'pulse 2s infinite' : 'none',
                    }}
                  />

                  {/* User info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{student.user_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{student.user_email}</p>
                    {student.manually_marked && (
                      <p className="text-xs text-blue-500 mt-0.5">
                        ✓ Marqué par {student.manually_marked_by}
                      </p>
                    )}
                  </div>

                  {/* Status badge */}
                  <Badge
                    variant="secondary"
                    className="text-xs flex-shrink-0"
                    style={{ backgroundColor: cfg.bgColor, color: cfg.color, border: `1px solid ${cfg.color}40` }}
                  >
                    <Icon className="h-3 w-3 mr-1" />
                    {cfg.label}
                  </Badge>

                  {/* Missed verifications alert */}
                  {student.missed_verifications > 0 && (
                    <div className="flex items-center gap-1 text-xs text-amber-500 flex-shrink-0" title="Vérifications manquées">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {student.missed_verifications}
                    </div>
                  )}

                  {/* Manual mark button for idle/absent students */}
                  {canMark && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs gap-1 flex-shrink-0 h-7"
                      onClick={() => markPresent(student.user_id)}
                      disabled={markingUserId === student.user_id}
                      title="Marquer comme présent (travail externe)"
                    >
                      {markingUserId === student.user_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <UserCheck className="h-3 w-3" />
                      )}
                      Présent
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {error && <p className="text-xs text-destructive text-center">{error}</p>}
      </CardContent>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Card>
  );
}
